import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  DollarSign, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Download,
  RefreshCw,
  CreditCard,
  TrendingUp,
  Calendar,
  Zap
} from 'lucide-react';
import { 
  getPayoutSummary, 
  getTransactionReport, 
  getPayoutReport, 
  createStandardPayout, 
  createInstantPayout,
  retryFailedPayout,
  type PayoutSummary,
  type TransactionReport,
  type PayoutReport
} from '@/lib/payouts';
import { formatCurrency } from '@/utils/formatCurrency';
import { useToast } from '@/components/ui/use-toast';

interface PayoutManagementProps {
  sellerId: string;
  sellerName?: string;
}

export function PayoutManagement({ sellerId, sellerName }: PayoutManagementProps) {
  const { toast } = useToast();
  const [summary, setSummary] = useState<PayoutSummary | null>(null);
  const [transactions, setTransactions] = useState<TransactionReport[]>([]);
  const [payouts, setPayouts] = useState<PayoutReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRequestingPayout, setIsRequestingPayout] = useState(false);

  useEffect(() => {
    fetchData();
  }, [sellerId]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      
      // Get payout summary
      const payoutSummary = await getPayoutSummary(sellerId);
      setSummary(payoutSummary);

      // Get recent transactions (last 30 days)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(endDate.getDate() - 30);
      
      const transactionData = await getTransactionReport(sellerId, startDate, endDate, 20);
      setTransactions(transactionData);

      const payoutData = await getPayoutReport(sellerId, startDate, endDate, 10);
      setPayouts(payoutData);

    } catch (error) {
      console.error('Error fetching payout data:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load payout data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleStandardPayout = async () => {
    try {
      setIsRequestingPayout(true);
      
      if (!summary?.pendingAmount || summary.pendingAmount <= 0) {
        toast({
          variant: "destructive",
          title: "No funds available",
          description: "You don't have any pending funds to withdraw.",
        });
        return;
      }

      const result = await createStandardPayout(sellerId);
      
      toast({
        title: "Payout requested successfully",
        description: `Your payout of ${formatCurrency(summary.pendingAmount)} has been requested. Expected arrival: ${result.expectedArrival.toLocaleDateString()}`,
      });

      // Refresh data
      fetchData();

    } catch (error) {
      console.error('Error requesting payout:', error);
      toast({
        variant: "destructive",
        title: "Payout failed",
        description: "There was an error processing your payout request. Please try again.",
      });
    } finally {
      setIsRequestingPayout(false);
    }
  };

  const handleInstantPayout = async () => {
    try {
      setIsRequestingPayout(true);
      
      if (!summary?.pendingAmount || summary.pendingAmount <= 0) {
        toast({
          variant: "destructive",
          title: "No funds available",
          description: "You don't have any pending funds to withdraw.",
        });
        return;
      }

      const result = await createInstantPayout(sellerId);
      
      toast({
        title: "Instant payout requested successfully",
        description: `Your instant payout of ${formatCurrency(summary.pendingAmount)} has been requested. Expected arrival: within 30 minutes`,
      });

      // Refresh data
      fetchData();

    } catch (error) {
      console.error('Error requesting instant payout:', error);
      toast({
        variant: "destructive",
        title: "Instant payout failed",
        description: "There was an error processing your instant payout request. Please try again.",
      });
    } finally {
      setIsRequestingPayout(false);
    }
  };

  const handleRetryPayout = async (payoutId: string) => {
    try {
      const result = await retryFailedPayout(payoutId);
      
      if (result.success) {
        toast({
          title: "Payout retry successful",
          description: "Your payout has been retried and is being processed.",
        });
        fetchData();
      } else {
        toast({
          variant: "destructive",
          title: "Retry failed",
          description: "Could not retry the payout. Please contact support.",
        });
      }
    } catch (error) {
      console.error('Error retrying payout:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "There was an error retrying your payout.",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'succeeded':
      case 'paid':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><AlertCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {[1, 2, 3].map(i => (
          <Card key={i}>
            <CardHeader>
              <div className="h-6 bg-muted animate-pulse rounded" />
            </CardHeader>
            <CardContent>
              <div className="h-20 bg-muted animate-pulse rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Payout Management</h1>
          {sellerName && (
            <p className="text-muted-foreground">Manage payouts for {sellerName}</p>
          )}
        </div>
        
        <Button variant="outline" size="sm" onClick={fetchData}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Payout Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Pending Balance</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(summary?.pendingAmount || 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Available for withdrawal</p>
              </div>
              <DollarSign className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Payouts</p>
                <p className="text-2xl font-bold">{summary?.totalPayouts || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {summary?.successfulPayouts || 0} successful
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Last Payout</p>
                <p className="text-2xl font-bold">
                  {summary?.lastPayoutDate ? summary.lastPayoutDate.toLocaleDateString() : 'None'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Most recent</p>
              </div>
              <Calendar className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Next Payout</p>
                <p className="text-2xl font-bold">
                  {summary?.nextPayoutDate ? summary.nextPayoutDate.toLocaleDateString() : 'Manual'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">Estimated</p>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Payout Actions */}
      {summary?.pendingAmount && summary.pendingAmount > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Request Payout</CardTitle>
            <CardDescription>
              You have {formatCurrency(summary.pendingAmount)} available for withdrawal
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5 text-blue-500" />
                  <h4 className="font-medium">Standard Payout</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  Free transfer that arrives in 2 business days
                </p>
                <Button 
                  onClick={handleStandardPayout} 
                  disabled={isRequestingPayout}
                  className="w-full"
                >
                  {isRequestingPayout ? 'Processing...' : 'Request Standard Payout'}
                </Button>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-500" />
                  <h4 className="font-medium">Instant Payout</h4>
                </div>
                <p className="text-sm text-muted-foreground">
                  1.5% fee, arrives within 30 minutes
                </p>
                <Button 
                  onClick={handleInstantPayout} 
                  disabled={isRequestingPayout}
                  variant="outline"
                  className="w-full"
                >
                  {isRequestingPayout ? 'Processing...' : 'Request Instant Payout'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Failed Payouts Alert */}
      {summary?.failedPayouts && summary.failedPayouts > 0 && (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            You have {summary.failedPayouts} failed payout{summary.failedPayouts !== 1 ? 's' : ''}. 
            Check the Payouts tab to retry them.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="transactions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="transactions">Recent Transactions</TabsTrigger>
          <TabsTrigger value="payouts">Payout History</TabsTrigger>
        </TabsList>

        <TabsContent value="transactions" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
              <CardDescription>
                Your recent sales and earnings (last 30 days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {transactions.length > 0 ? (
                <div className="space-y-4">
                  {transactions.map((transaction) => (
                    <div key={transaction.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <DollarSign className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">{transaction.sequenceId}</p>
                          <p className="text-sm text-muted-foreground">
                            {transaction.createdAt.toLocaleDateString()} • {transaction.buyerId}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">
                            {formatCurrency(transaction.sellerAmount)}
                          </span>
                          {getStatusBadge(transaction.status)}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {formatCurrency(transaction.amount)} - {formatCurrency(transaction.platformFee)} fee
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No transactions found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="payouts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Payout History</CardTitle>
              <CardDescription>
                Your recent payout requests and their status
              </CardDescription>
            </CardHeader>
            <CardContent>
              {payouts.length > 0 ? (
                <div className="space-y-4">
                  {payouts.map((payout) => (
                    <div key={payout.id} className="flex items-center justify-between p-4 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <ArrowRight className="h-5 w-5" />
                        </div>
                        <div>
                          <p className="font-medium">Payout</p>
                          <p className="text-sm text-muted-foreground">
                            {payout.createdAt.toLocaleDateString()}
                            {payout.arrivalDate && ` • Arrived ${payout.arrivalDate.toLocaleDateString()}`}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">
                            {formatCurrency(payout.amount)}
                          </span>
                          {getStatusBadge(payout.status)}
                        </div>
                        {payout.status === 'failed' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRetryPayout(payout.id)}
                            className="mt-1"
                          >
                            Retry
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Download className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No payouts found</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
} 